import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Plus, Copy, Trash2, KeyRound, Webhook as WebhookIcon,
  Send, Power, CheckCircle2, AlertCircle, RefreshCw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

function formatApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => {
      const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
      return field ? `${field}: ${d.msg}` : d.msg;
    }).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch { return "—"; }
}

export default function InstructorIntegrations() {
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [supportedEvents, setSupportedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // New-key + new-webhook UI state
  const [newKeyName, setNewKeyName] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // {name, plaintext_key, prefix}

  const [newHookName, setNewHookName] = useState("");
  const [newHookUrl, setNewHookUrl] = useState("");
  const [newHookEvents, setNewHookEvents] = useState([]);
  const [savingHook, setSavingHook] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null); // {name, secret}

  const load = async () => {
    try {
      const [{ data: keys }, { data: hooks }] = await Promise.all([
        api.get("/admin/api-keys"),
        api.get("/admin/webhooks"),
      ]);
      setApiKeys(keys.api_keys || []);
      setWebhooks(hooks.webhooks || []);
      setSupportedEvents(hooks.supported_events || []);
    } catch {
      toast.error("Couldn't load integrations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!newKeyName.trim()) { toast.error("Name is required"); return; }
    setSavingKey(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: newKeyName.trim() });
      setRevealedKey(data);
      setNewKeyName("");
      await load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't create key")); }
    finally { setSavingKey(false); }
  };

  const revokeKey = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? Any script using it will stop working immediately.`)) return;
    try { await api.delete(`/admin/api-keys/${id}`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(formatApiError(e, "Couldn't revoke")); }
  };

  const toggleHookEvent = (event) => {
    setNewHookEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  const createHook = async () => {
    if (!newHookName.trim() || !newHookUrl.trim() || newHookEvents.length === 0) {
      toast.error("Name, URL, and at least 1 event required");
      return;
    }
    setSavingHook(true);
    try {
      const { data } = await api.post("/admin/webhooks", {
        name: newHookName.trim(), url: newHookUrl.trim(), events: newHookEvents,
      });
      setRevealedSecret({ name: data.name, secret: data.secret });
      setNewHookName(""); setNewHookUrl(""); setNewHookEvents([]);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't create webhook")); }
    finally { setSavingHook(false); }
  };

  const toggleHookActive = async (id, active) => {
    try { await api.patch(`/admin/webhooks/${id}`, { active }); await load(); }
    catch (e) { toast.error(formatApiError(e, "Couldn't toggle")); }
  };

  const testHook = async (id, name) => {
    try {
      const { data } = await api.post(`/admin/webhooks/${id}/test`);
      if (data.delivered) toast.success(`Test ping delivered to "${name}" (HTTP ${data.status_code})`);
      else toast.error(`Test failed: ${data.error || "no response"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e, "Couldn't test")); }
  };

  const deleteHook = async (id, name) => {
    if (!window.confirm(`Delete webhook "${name}"?`)) return;
    try { await api.delete(`/admin/webhooks/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e, "Couldn't delete")); }
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading) {
    return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading integrations…</div></div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-10" data-testid="integrations-page">
        <Link to="/instructor/launch" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-red-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Mission control
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Instructor tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3">Integrations</h1>
        <p className="text-sm text-zinc-700 max-w-2xl leading-relaxed mb-2">
          Connect APM to n8n, Zapier, Make, or any tool that speaks HTTP. Two paths:
        </p>
        <ul className="text-sm text-zinc-700 space-y-1 mb-8 list-disc pl-5">
          <li><strong>API keys</strong> — external tools READ data from APM (dashboard, leads, drip stats, etc.)</li>
          <li><strong>Webhooks</strong> — APM PUSHES events to external tools (new lead, payment, prospect status change, drip step sent)</li>
        </ul>

        {/* Revealed key modal-ish banner */}
        {revealedKey && (
          <div className="card-tech p-5 mb-6 border-l-4 border-amber-500 bg-amber-50" data-testid="revealed-key">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-heading font-bold">Save this key — it won't be shown again.</h3>
                  <p className="text-xs text-zinc-700">Copy it into n8n now. We only store the hash.</p>
                </div>
              </div>
              <button onClick={() => setRevealedKey(null)} className="text-zinc-500 hover:text-red-600 text-sm">Dismiss</button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <code className="bg-white border border-zinc-300 px-3 py-2 text-xs font-mono flex-1 break-all" data-testid="revealed-key-value">{revealedKey.plaintext_key}</code>
              <button onClick={() => copy(revealedKey.plaintext_key, "Key")} className="btn-outline text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
          </div>
        )}

        {/* API KEYS section */}
        <section className="card-tech p-6 mb-8" data-testid="apikeys-section">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-red-600" />
            <div className="overline">Step 1 — Authentication</div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">API keys</h2>
          <p className="text-xs text-zinc-600 mb-5">Generate one per integration. Use as <code className="bg-zinc-100 px-1.5 py-0.5">Authorization: Bearer apm_xxx</code> header.</p>

          <div className="flex gap-2 mb-4">
            <input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g. n8n Mission Control digest"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm"
              data-testid="apikey-name-input"
            />
            <button onClick={createKey} disabled={savingKey} className="btn-primary text-xs" data-testid="apikey-create">
              <Plus className="w-3.5 h-3.5" /> {savingKey ? "Creating…" : "Generate key"}
            </button>
          </div>

          {apiKeys.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No API keys yet. Generate one above to start using n8n.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {apiKeys.map((k) => (
                <li key={k.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`apikey-${k.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{k.name}</span>
                      {k.revoked_at && <span className="text-[10px] font-mono uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5">REVOKED</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                      {k.prefix}… · created {fmtRelative(k.created_at)} · last used {fmtRelative(k.last_used_at)}
                    </div>
                  </div>
                  {!k.revoked_at && (
                    <button onClick={() => revokeKey(k.id, k.name)} className="text-zinc-500 hover:text-red-600 p-1.5" title="Revoke" data-testid={`apikey-revoke-${k.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* WEBHOOKS section */}
        <section className="card-tech p-6 mb-8" data-testid="webhooks-section">
          <div className="flex items-center gap-2 mb-1">
            <WebhookIcon className="w-5 h-5 text-red-600" />
            <div className="overline">Step 2 — Push events</div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Webhooks</h2>
          <p className="text-xs text-zinc-600 mb-5">
            APM POSTs JSON to your URL with <code className="bg-zinc-100 px-1.5 py-0.5">X-APM-Signature: sha256=...</code>.
            Verify HMAC using the shared secret shown when you create the webhook.
          </p>

          {/* Revealed secret banner */}
          {revealedSecret && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-4 mb-4" data-testid="revealed-secret">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <p className="text-xs font-semibold">Webhook secret for "{revealedSecret.name}" — save it now!</p>
                </div>
                <button onClick={() => setRevealedSecret(null)} className="text-zinc-500 hover:text-red-600 text-xs">Dismiss</button>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-zinc-300 px-3 py-1.5 text-xs font-mono flex-1 break-all" data-testid="revealed-secret-value">{revealedSecret.secret}</code>
                <button onClick={() => copy(revealedSecret.secret, "Secret")} className="btn-outline text-xs"><Copy className="w-3 h-3" /></button>
              </div>
            </div>
          )}

          {/* New webhook form */}
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            <input
              value={newHookName}
              onChange={(e) => setNewHookName(e.target.value)}
              placeholder="n8n hot prospect alert"
              className="border border-zinc-300 px-3 py-2 text-sm"
              data-testid="webhook-name-input"
            />
            <input
              value={newHookUrl}
              onChange={(e) => setNewHookUrl(e.target.value)}
              placeholder="https://your-n8n.cloud/webhook/abc123"
              className="border border-zinc-300 px-3 py-2 text-sm"
              data-testid="webhook-url-input"
            />
          </div>

          <div className="mb-3">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1.5">Events</div>
            <div className="flex flex-wrap gap-1.5">
              {supportedEvents.map((ev) => (
                <button
                  key={ev}
                  onClick={() => toggleHookEvent(ev)}
                  className={`text-[11px] font-mono px-2.5 py-1 border transition-colors ${
                    newHookEvents.includes(ev)
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-zinc-700 border-zinc-300 hover:border-red-300"
                  }`}
                  data-testid={`webhook-event-${ev}`}
                >
                  {ev}
                </button>
              ))}
            </div>
          </div>

          <button onClick={createHook} disabled={savingHook} className="btn-primary text-xs mb-6" data-testid="webhook-create">
            <Plus className="w-3.5 h-3.5" /> {savingHook ? "Creating…" : "Create webhook"}
          </button>

          {webhooks.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">No webhooks yet. Add one above to push events to n8n or Zapier.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {webhooks.map((h) => (
                <li key={h.id} className="py-3" data-testid={`webhook-${h.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{h.name}</span>
                        {h.active
                          ? <span className="text-[10px] font-mono uppercase tracking-widest bg-green-100 text-green-700 px-1.5 py-0.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ACTIVE</span>
                          : <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-100 text-zinc-600 px-1.5 py-0.5">PAUSED</span>
                        }
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono break-all">{h.url}</div>
                      <div className="text-[11px] text-zinc-600 mt-1">
                        Events: {h.events.map((e) => <code key={e} className="bg-zinc-100 px-1 mr-1">{e}</code>)}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-1">
                        {h.fire_count || 0} delivered · {h.failure_count || 0} failed ·
                        {" "}last fire {fmtRelative(h.last_fired_at)}
                        {h.last_status && <span className="ml-1">· <span className={h.last_status.startsWith("error") ? "text-red-600" : "text-green-600"}>{h.last_status}</span></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => testHook(h.id, h.name)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100" title="Send test ping" data-testid={`webhook-test-${h.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggleHookActive(h.id, !h.active)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100" title={h.active ? "Pause" : "Resume"} data-testid={`webhook-toggle-${h.id}`}>
                        <Power className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteHook(h.id, h.name)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50" title="Delete" data-testid={`webhook-delete-${h.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Setup guide link */}
        <div className="card-tech p-6">
          <div className="overline mb-2"><span className="red-bar" />Setup walkthrough</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Connect n8n in 15 minutes</h2>
          <p className="text-sm text-zinc-700 mb-4">
            Step-by-step instructions + 2 ready-to-import workflow JSON files in
            <code className="bg-zinc-100 px-1.5 py-0.5 mx-1">/app/memory/LAUNCH_PLAYBOOK.md Section 4</code>.
          </p>
          <div className="flex flex-wrap gap-2">
            <a href="/n8n-workflows/apm-daily-digest.json" download className="btn-outline text-xs" data-testid="download-daily-digest">
              <ExternalLink className="w-3 h-3" /> Download "Daily Digest" workflow
            </a>
            <a href="/n8n-workflows/apm-hot-prospect-alert.json" download className="btn-outline text-xs" data-testid="download-prospect-alert">
              <ExternalLink className="w-3 h-3" /> Download "Hot Prospect Alert" workflow
            </a>
            <Link to="/instructor/launch" className="btn-outline text-xs">
              <RefreshCw className="w-3 h-3" /> Back to Mission Control
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
